/**
 * `JourneyDrawer` — right-side slide-over opened by the readiness
 * `JourneyStrip` nodes (Find / Read / Understand / Act).
 *
 * Rewritten 2026-10-02 to tell a story. Collapsed rows read as one
 * plain sentence per agent ("Claude-User read your pricing page 36
 * times and your home page 26 times."); expanded rows group pages
 * into readable kinds (Home / Pricing / Product / Blog / Docs /
 * Contact + signup / Other) and surface search terms, skills used,
 * and a quiet amber "likely impersonation" line when the agent's
 * identity has probe traffic or spoof signals.
 *
 * All data comes from `overview.funnel.by_agent`. Probe paths are
 * filtered on the server (`isProbePath` in `@sill/types`); the UI
 * only renders what's in `top_paths` / `probe_examples`.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { AgentOverviewResponse } from '@sill/types/api';
import { discoveryKindLabel } from '@sill/types/api';
import {
  agentIdentityStatus,
  impersonationHitsFromSignals,
  agentIdentityStatusExplanation,
  agentIdentityStatusLabel,
  looksLikeKnownBotName,
  type AgentIdentityStatus,
} from '@sill/types';

import { Drawer } from '../../components/atoms/Drawer.js';
import { useC } from '../../lib/theme.js';
import type { SetView } from '../../app/view-router.js';
import { monoFont, sansFont, serifFont } from './fonts.js';

export type JourneyStepKey = 'find' | 'read' | 'understand' | 'act';

type ByAgent = AgentOverviewResponse['funnel']['by_agent'][number];

export interface JourneyDrawerProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly step: JourneyStepKey | null;
  readonly overview: AgentOverviewResponse | null;
  readonly site_domain?: string | null;
  readonly setView: SetView;
  /** Opens the Agents view on the "Probes" tab. Used by the single
   *  coral impersonator line ("Likely impersonator · 81 probe
   *  requests. See probes →"). */
  readonly onOpenProbes?: () => void;
}

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function nameFor(agent: ByAgent): string {
  if (agent.inferred_name !== null && agent.inferred_name.length > 0) return agent.inferred_name;
  if (agent.agent_id !== null && agent.agent_id.length > 0) return agent.agent_id;
  return 'Unknown agent';
}

/** Classify the agent's identity using the shared pure classifier.
 *  Inputs come from the overview response's per-agent fields:
 *   - signed_verified_hits / cf_verified_hits (2026-10-02 — read from
 *     crawler_event in the overview SQL),
 *   - probe_hits,
 *   - spoof_signals' sig_invalid / typo_ua / cf_unverified,
 *   - known-bot heuristic: registry match OR inferred_name starts with
 *     a documented AI/search bot prefix. */
function statusOf(agent: ByAgent): AgentIdentityStatus {
  const sp = agent.spoof_signals ?? {};
  const has_registry = agent.agent_id !== null && agent.agent_id.length > 0;
  const has_name = looksLikeKnownBotName(agent.inferred_name);
  const known = has_registry || has_name;
  return agentIdentityStatus({
    has_known_bot_name: known,
    signed_verified_hits: Number((agent as { signed_verified_hits?: number }).signed_verified_hits ?? 0),
    signature_invalid_hits: Number(sp['sig_invalid'] ?? 0),
    cf_verified_hits: Number((agent as { cf_verified_hits?: number }).cf_verified_hits ?? 0),
    cf_unverified_hits: Number(sp['cf_unverified'] ?? 0),
    typo_ua_hits: Number(sp['typo_ua'] ?? 0),
    probe_hits: Number((agent as { probe_hits?: number }).probe_hits ?? 0),
    impersonation_hits: impersonationHitsFromSignals(sp),
  });
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

/* -------------------------------------------------------------------------- */
/* Page-group classifier (deterministic, closed set)                          */
/* -------------------------------------------------------------------------- */

const PAGE_GROUPS = ['home', 'pricing', 'product', 'blog', 'docs', 'contact', 'other'] as const;
type PageGroup = (typeof PAGE_GROUPS)[number];

const PAGE_GROUP_LABEL: Readonly<Record<PageGroup, string>> = {
  home: 'Home',
  pricing: 'Pricing',
  product: 'Product and feature pages',
  blog: 'Blog and articles',
  docs: 'Docs',
  contact: 'Contact, demo and signup',
  other: 'Other pages',
};

/** Classify a path into one of the readable groups. Deterministic. */
function groupOf(path: string): PageGroup {
  const lower = path.toLowerCase();
  if (lower === '/' || lower === '/index' || lower === '/index.html' || lower === '/home') return 'home';
  if (/^\/pricing\/?$|^\/plans\/?$|^\/prices\/?$/.test(lower)) return 'pricing';
  if (/^\/docs?\//.test(lower) || /^\/documentation\//.test(lower) || /^\/developers?\//.test(lower)) return 'docs';
  if (/^\/blog(\/|$)/.test(lower) || /^\/articles(\/|$)/.test(lower) || /^\/news(\/|$)/.test(lower) || /^\/post\//.test(lower)) return 'blog';
  if (/^\/contact(\/|$)/.test(lower) || /^\/demo(\/|$)/.test(lower) || /^\/signup(\/|$)/.test(lower) || /^\/register(\/|$)/.test(lower) || /^\/get[-_]started(\/|$)/.test(lower) || /^\/trial(\/|$)/.test(lower)) return 'contact';
  if (/^\/products?\//.test(lower) || /^\/collections?\//.test(lower) || /^\/features(\/|$)/.test(lower) || /^\/solutions?\//.test(lower) || /^\/integrations?\//.test(lower) || /^\/use[-_]cases?(\/|$)/.test(lower)) return 'product';
  return 'other';
}

/** Shorten a path for display: `/products/olive-oil` → "olive oil". */
function prettyPath(path: string): string {
  if (path === '/' || path === '') return 'home page';
  const bits = path.replace(/^\/+|\/+$/g, '').split('/');
  const last = bits[bits.length - 1] ?? '';
  if (last.length === 0) return path;
  return last.replace(/[-_]/g, ' ');
}

/** Build the plain-sentence collapsed summary for an agent.
 *
 *  2026-10-02 — when the agent is a likely impersonator OR most of its
 *  requests are probes, the sentence LEADS with the probe behaviour
 *  ("Someone using the name <X> tried to read secret files 48 times,
 *  for example /.env …. Real <X> doesn't do this.") and never
 *  describes probe paths as pages the agent read. Legitimate pages (if
 *  any) follow as a short trailer ("It also loaded 4 other pages.").
 */
function readStory(agent: ByAgent): string {
  const paths = agent.top_paths ?? []; // Already probe-filtered by API.
  const name = nameFor(agent);
  const status = statusOf(agent);
  const probe_hits = Number((agent as { probe_hits?: number }).probe_hits ?? 0);
  const probe_examples = ((agent as { probe_examples?: readonly { path: string; n: number }[] }).probe_examples ?? []).slice(0, 2).map((p) => p.path);
  const total_pages = paths.reduce((s, p) => s + p.n, 0);
  const probes_dominate = probe_hits > 0 && probe_hits >= total_pages;

  if (status === 'likely_impersonator' || probes_dominate) {
    const examples = probe_examples.length > 0 ? ` for example ${probe_examples.join(' and ')}` : '';
    const lead = `Someone using the name ${name} tried to read secret files ${fmtInt(probe_hits)} ${probe_hits === 1 ? 'time' : 'times'}${examples}. Real ${name} doesn't do this.`;
    if (paths.length > 0) {
      const extra = `It also loaded ${fmtInt(paths.length)} other ${paths.length === 1 ? 'page' : 'pages'}.`;
      return `${lead} ${extra}`;
    }
    return lead;
  }

  if (paths.length === 0) {
    if ((agent.sessions ?? 0) > 0) {
      return `${name} loaded ${fmtInt(agent.sessions)} ${agent.sessions === 1 ? 'page' : 'pages'} on your site.`;
    }
    return `${name} visited your site.`;
  }
  // Pick the top two meaningful pages for the sentence.
  const top = paths.slice(0, 2);
  const first = top[0]!;
  const groups = top.map((p) => groupOf(p.path));
  const label = (p: { path: string; n: number }, g: PageGroup): string => {
    if (g === 'home') return `your home page ${fmtInt(p.n)} ${p.n === 1 ? 'time' : 'times'}`;
    if (g === 'pricing') return `your pricing page ${fmtInt(p.n)} ${p.n === 1 ? 'time' : 'times'}`;
    if (g === 'docs') return `your docs ${fmtInt(p.n)} ${p.n === 1 ? 'time' : 'times'}`;
    if (g === 'blog') return `${prettyPath(p.path)} ${fmtInt(p.n)} ${p.n === 1 ? 'time' : 'times'}`;
    return `${prettyPath(p.path)} ${fmtInt(p.n)} ${p.n === 1 ? 'time' : 'times'}`;
  };
  if (top.length === 1) {
    return `${name} read ${label(first, groups[0]!)}.`;
  }
  const second = top[1]!;
  return `${name} read ${label(first, groups[0]!)} and ${label(second, groups[1]!)}.`;
}

function findStory(agent: ByAgent): string {
  const name = nameFor(agent);
  const entries = Object.entries(agent.discovery_by_kind ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (entries.length === 0) {
    const hits = agent.discovery_hits ?? 0;
    return `${name} fetched ${fmtInt(hits)} discovery ${hits === 1 ? 'file' : 'files'}.`;
  }
  if (entries.length === 1) {
    const [k, n] = entries[0]!;
    return `${name} fetched your ${discoveryKindLabel(k)} ${fmtInt(Number(n))} ${Number(n) === 1 ? 'time' : 'times'}.`;
  }
  const named = entries.slice(0, 2).map(([k]) => `your ${discoveryKindLabel(k)}`);
  const more = entries.length > 2 ? ` and ${entries.length - 2} other ${entries.length - 2 === 1 ? 'file' : 'files'}` : '';
  return `${name} fetched ${named.join(' and ')}${more}.`;
}

function understandStory(agent: ByAgent): string {
  const name = nameFor(agent);
  const terms = agent.search_terms ?? [];
  if (terms.length > 0) {
    const t = terms[0]!;
    const extra = terms.length === 1 ? '' : terms.length === 2 ? ' and 1 other query' : ` and ${terms.length - 1} other queries`;
    const quoted = `"${t.term}"`;
    const n = Number(t.n);
    return `${name} searched for ${quoted} ${fmtInt(n)} ${n === 1 ? 'time' : 'times'}${extra}.`;
  }
  if ((agent.product ?? 0) > 0 || (agent.search ?? 0) > 0) {
    const bits: string[] = [];
    if ((agent.product ?? 0) > 0) bits.push(`${fmtInt(agent.product)} product ${agent.product === 1 ? 'page' : 'pages'}`);
    if ((agent.search ?? 0) > 0) bits.push(`${fmtInt(agent.search)} ${agent.search === 1 ? 'search' : 'searches'}`);
    return `${name} reached ${bits.join(' and ')}.`;
  }
  return `${name} did not search this week.`;
}

function actStory(agent: ByAgent): string {
  const name = nameFor(agent);
  const skills = Object.entries(agent.actions_by_skill ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (skills.length === 0 && (agent.actions ?? 0) === 0) {
    return `${name} did not request any skill this week.`;
  }
  if (skills.length === 1) {
    const [k, n] = skills[0]!;
    return `${name} called ${k.replace(/_/g, ' ')} ${fmtInt(Number(n))} ${Number(n) === 1 ? 'time' : 'times'}. ${fmtInt(agent.approved)} approved.`;
  }
  const named = skills.slice(0, 2).map(([k, n]) => `${k.replace(/_/g, ' ')} ×${fmtInt(Number(n))}`);
  const more = skills.length > 2 ? ` and ${skills.length - 2} ${skills.length - 2 === 1 ? 'other' : 'others'}` : '';
  return `${name} called ${named.join(' and ')}${more}. ${fmtInt(agent.approved)} approved.`;
}

/** Impersonation / spoof note. Returns `null` when there's nothing
 *  to say. Rendered quietly in amber on the agent row.
 *
 *  When the identity-status classifier calls this agent an impersonator,
 *  the probe lead is already in the row's main sentence (`readStory`);
 *  this helper then returns the trailing network / signature detail only,
 *  so the row doesn't say the same thing twice.
 */
function impersonationLine(agent: ByAgent, status: AgentIdentityStatus): string | null {
  const probe_hits = agent.probe_hits ?? 0;
  const sp = agent.spoof_signals ?? {};
  const cf = Number(sp['cf_unverified'] ?? 0);
  const typo = Number(sp['typo_ua'] ?? 0);
  const sig_invalid = Number(sp['sig_invalid'] ?? 0);
  if (probe_hits === 0 && cf === 0 && typo === 0 && sig_invalid === 0) return null;
  const name = nameFor(agent);
  const bits: string[] = [];
  // Probes are LED in the main sentence when status is impersonator —
  // skip them here to avoid repeating the same number.
  if (probe_hits > 0 && status !== 'likely_impersonator') {
    const examples = (agent.probe_examples ?? []).slice(0, 3).map((p) => p.path).join(', ');
    const example_tail = examples.length > 0 ? ` (e.g. ${examples})` : '';
    bits.push(`Also tried to read secret files ${fmtInt(probe_hits)} ${probe_hits === 1 ? 'time' : 'times'}${example_tail}. Real ${name} doesn't do this — likely someone using its name.`);
  }
  if (cf > 0) bits.push(`The network could not verify this is really ${name}.`);
  if (typo > 0) bits.push(`The identity string looks like a misspelled version of ${name}.`);
  if (sig_invalid > 0) bits.push(`Request signatures didn't verify.`);
  if (bits.length === 0) return null;
  return bits.join(' ');
}

/* -------------------------------------------------------------------------- */
/* Row rendering                                                              */
/* -------------------------------------------------------------------------- */

function hostForLink(site_domain: string | null | undefined): string | null {
  if (site_domain === null || site_domain === undefined || site_domain.length === 0) return null;
  // Already a bare host — no scheme.
  return site_domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function PathLink({ path, host }: { path: string; host: string | null }): JSX.Element {
  const C = useC();
  if (host === null) {
    return <span style={{ fontFamily: monoFont, fontSize: 11.5, color: C.text }}>{path}</span>;
  }
  const href = `https://${host}${path.startsWith('/') ? path : `/${path}`}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ fontFamily: monoFont, fontSize: 11.5, color: C.text, textDecoration: 'underline' }}
    >
      {path}
    </a>
  );
}

interface AgentRowProps {
  readonly agent: ByAgent;
  readonly step: JourneyStepKey;
  readonly host: string | null;
  readonly summary: string;
  readonly onOpenDetails: () => void;
  readonly onOpenProbes?: () => void;
}

function AgentRow({ agent, step, host, summary, onOpenDetails, onOpenProbes }: AgentRowProps): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(false);
  const status = statusOf(agent);
  const statusLabel = agentIdentityStatusLabel(status);
  const name = nameFor(agent);
  const statusExplainer = agentIdentityStatusExplanation(status, name);
  const impersonation = impersonationLine(agent, status);
  const probe_hits = Number((agent as { probe_hits?: number }).probe_hits ?? 0);
  const impersonatorLine =
    status === 'likely_impersonator'
      ? `Likely impersonator · ${probe_hits.toLocaleString('en-US')} probe ${probe_hits === 1 ? 'request' : 'requests'}.`
      : null;

  // Group pages for the "Read" expansion.
  const groups = new Map<PageGroup, Array<{ path: string; n: number }>>();
  for (const p of agent.top_paths ?? []) {
    const g = groupOf(p.path);
    const list = groups.get(g) ?? [];
    list.push({ path: p.path, n: p.n });
    groups.set(g, list);
  }
  const groupSums = new Map<PageGroup, number>();
  for (const [g, list] of groups) groupSums.set(g, list.reduce((s, v) => s + v.n, 0));

  const row_id = `journey-row-${agent.agent_key}`;

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={row_id}
        className="w-full"
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: 10,
          alignItems: 'start',
          textAlign: 'left',
          background: 'transparent',
          border: 'none',
          padding: '12px 14px',
          color: C.text,
          fontFamily: sansFont,
          cursor: 'pointer',
        }}
      >
        <div style={{ marginTop: 2, color: C.muted }}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{name}</div>
            {statusLabel.length > 0 ? (
              <div
                data-testid="journey-identity-status"
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: status === 'likely_impersonator' ? C.coralFg : C.muted,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}
              >
                {statusLabel}
              </div>
            ) : null}
          </div>
          <div style={{ fontSize: 12.5, color: C.text, lineHeight: 1.5, marginTop: 2 }}>
            {summary}
          </div>
          {impersonatorLine !== null ? (
            <div
              data-testid="journey-impersonator-line"
              style={{
                fontSize: 11.5,
                color: C.coralFg,
                lineHeight: 1.5,
                marginTop: 6,
              }}
            >
              {impersonatorLine}
              {typeof onOpenProbes === 'function' ? (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenProbes(); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      color: C.coralFg,
                      fontFamily: monoFont,
                      fontSize: 11,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    See probes →
                  </button>
                </>
              ) : null}
            </div>
          ) : impersonation !== null ? (
            <div
              style={{
                fontSize: 11.5,
                color: C.amberFg,
                lineHeight: 1.5,
                marginTop: 6,
              }}
            >
              {impersonation}
            </div>
          ) : null}
        </div>
      </button>
      {open ? (
        <div
          id={row_id}
          style={{
            padding: '12px 14px 14px 38px',
            borderTop: `1px solid ${C.borderSoft}`,
          }}
        >
          {statusExplainer.length > 0 ? (
            <div
              data-testid="journey-identity-explainer"
              style={{
                fontFamily: sansFont,
                fontSize: 11.5,
                color: C.muted,
                lineHeight: 1.5,
                marginBottom: 10,
              }}
            >
              <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginRight: 6 }}>
                How we know
              </span>
              {statusExplainer}
            </div>
          ) : null}
          {step === 'read' || step === 'understand' ? (
            <ReadExpansion agent={agent} groups={groups} groupSums={groupSums} host={host} />
          ) : null}
          {step === 'find' ? <FindExpansion agent={agent} /> : null}
          {step === 'understand' ? <SearchExpansion agent={agent} /> : null}
          {step === 'act' ? <ActExpansion agent={agent} /> : null}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={onOpenDetails}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 12.5,
                textDecoration: 'underline',
                textDecorationColor: C.dim,
                textUnderlineOffset: '3px',
                cursor: 'pointer',
              }}
            >
              Agent details →
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Kicker({ children }: { children: ReactNode }): JSX.Element {
  const C = useC();
  return (
    <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 6 }}>
      {children}
    </div>
  );
}

function ReadExpansion({
  agent,
  groups,
  groupSums,
  host,
}: {
  agent: ByAgent;
  groups: Map<PageGroup, Array<{ path: string; n: number }>>;
  groupSums: Map<PageGroup, number>;
  host: string | null;
}): JSX.Element {
  const C = useC();
  const ordered = PAGE_GROUPS.filter((g) => (groupSums.get(g) ?? 0) > 0);
  if (ordered.length === 0) {
    return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No page paths recorded for this agent in the window.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Kicker>Pages this agent loaded</Kicker>
      {ordered.map((g) => {
        const list = groups.get(g) ?? [];
        const sum = groupSums.get(g) ?? 0;
        return (
          <div key={g}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.text, fontWeight: 500 }}>{PAGE_GROUP_LABEL[g]}</div>
              <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtInt(sum)}</div>
            </div>
            <ul style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
              {list.slice(0, 5).map((p) => (
                <li key={p.path} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '2px 0' }}>
                  <PathLink path={p.path} host={host} />
                  <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtInt(p.n)}</span>
                </li>
              ))}
            </ul>
            {list.length > 5 ? (
              <div style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 2 }}>
                + {fmtInt(list.length - 5)} more
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function FindExpansion({ agent }: { agent: ByAgent }): JSX.Element {
  const C = useC();
  const entries = Object.entries(agent.discovery_by_kind ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (entries.length === 0) {
    return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No discovery fetches recorded for this agent.</div>;
  }
  return (
    <div>
      <Kicker>Discovery files fetched</Kicker>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {entries.map(([k, n]) => (
          <li key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
            <span>{discoveryKindLabel(k)}</span>
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtInt(Number(n))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchExpansion({ agent }: { agent: ByAgent }): JSX.Element {
  const C = useC();
  const terms = agent.search_terms ?? [];
  if (terms.length === 0) {
    return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 10 }}>No search queries recorded.</div>;
  }
  return (
    <div style={{ marginTop: 10 }}>
      <Kicker>Search queries</Kicker>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {terms.slice(0, 10).map((t) => (
          <li key={t.term} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
            <span>&ldquo;{t.term}&rdquo;</span>
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtInt(t.n)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActExpansion({ agent }: { agent: ByAgent }): JSX.Element {
  const C = useC();
  const skills = Object.entries(agent.actions_by_skill ?? {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));
  if (skills.length === 0) {
    return <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted }}>No skills were called by this agent in the window.</div>;
  }
  return (
    <div>
      <Kicker>Skills called</Kicker>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {skills.map(([k, n]) => (
          <li key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontFamily: sansFont, fontSize: 12.5, color: C.text }}>
            <span>{k.replace(/_/g, ' ')}</span>
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{fmtInt(Number(n))}</span>
          </li>
        ))}
      </ul>
      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 8 }}>
        {fmtInt(agent.approved)} approved of {fmtInt(agent.actions)} requested.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Drawer shell                                                               */
/* -------------------------------------------------------------------------- */

function titleFor(step: JourneyStepKey): { kicker: string; title: string; lede: string } {
  if (step === 'find') {
    return {
      kicker: 'Journey · Find',
      title: 'AI agents that fetched your discovery files',
      lede: 'Agent card, llms.txt, UCP profile, MCP card, AI catalog, agent page, and other agent files.',
    };
  }
  if (step === 'read') {
    return {
      kicker: 'Journey · Read',
      title: 'AI agents that loaded your pages',
      lede: 'What each agent actually read on your site, grouped by page kind.',
    };
  }
  if (step === 'understand') {
    return {
      kicker: 'Journey · Understand',
      title: 'AI agents that reached products or search',
      lede: 'Product and search sessions, with the queries each agent ran.',
    };
  }
  return {
    kicker: 'Journey · Act',
    title: 'AI agents that used a skill',
    lede: 'Skill calls per agent and how many were approved.',
  };
}

function footerModeFor(step: JourneyStepKey): 'agents' | 'demand' | 'mandates' {
  if (step === 'understand') return 'demand';
  if (step === 'act') return 'mandates';
  return 'agents';
}

function filterRows(step: JourneyStepKey, overview: AgentOverviewResponse): readonly ByAgent[] {
  const by = overview.funnel.by_agent;
  if (step === 'find') return by.filter((a) => (a.discovery_hits ?? 0) > 0);
  if (step === 'read') return by.filter((a) => (a.sessions ?? 0) > 0 || (a.top_paths ?? []).length > 0);
  if (step === 'understand') return by.filter((a) => (a.product ?? 0) > 0 || (a.search ?? 0) > 0 || (a.search_terms ?? []).length > 0);
  return by.filter((a) => (a.actions ?? 0) > 0);
}

function summaryFor(step: JourneyStepKey, agent: ByAgent): string {
  if (step === 'find') return findStory(agent);
  if (step === 'read') return readStory(agent);
  if (step === 'understand') return understandStory(agent);
  return actStory(agent);
}

export function JourneyDrawer(props: JourneyDrawerProps): JSX.Element {
  const C = useC();
  const { open, onClose, step, overview, site_domain, setView, onOpenProbes } = props;
  const show_step: JourneyStepKey = step ?? 'read';
  const copy = titleFor(show_step);
  const rows = overview === null ? [] : filterRows(show_step, overview);
  const host = hostForLink(site_domain ?? null);
  const defaultProbes = (): void => {
    onClose();
    setView('agents', { agent_kind: 'ai', agents_mode: 'agents' });
  };

  return (
    <Drawer open={open} onClose={onClose} data-testid="readiness-journey-drawer">
      <div style={{ fontFamily: sansFont, color: C.text, padding: '24px 28px' }} aria-label={copy.title}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>
          {copy.kicker.toUpperCase()}
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 24, letterSpacing: -0.4, marginBottom: 6 }}>
          {copy.title}
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.5 }}>
          {copy.lede}
        </div>

        {rows.length === 0 ? (
          <div
            style={{
              padding: '18px 20px',
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              fontSize: 13,
              color: C.muted,
            }}
          >
            {overview === null
              ? 'Run your first check — this view fills in once the Worker or plugin has seen traffic.'
              : show_step === 'find'
                ? 'No AI agent has fetched a discovery file this week.'
                : show_step === 'read'
                  ? 'No page loads yet. Agents that fetch without running JavaScript show in traffic only.'
                  : show_step === 'understand'
                    ? 'No AI agent has reached a product or search page this week.'
                    : 'No AI agent has used a skill this week.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map((row) => (
              <AgentRow
                key={row.agent_key}
                agent={row}
                step={show_step}
                host={host}
                summary={summaryFor(show_step, row)}
                onOpenDetails={() => {
                  onClose();
                  setView('agents', { agent_kind: 'ai', agents_mode: 'agents', agent: row.agent_key });
                }}
                onOpenProbes={onOpenProbes ?? defaultProbes}
              />
            ))}
          </div>
        )}

        <div style={{ marginTop: 20, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 14 }}>
          <button
            type="button"
            onClick={() => {
              const mode = footerModeFor(show_step);
              onClose();
              if (mode === 'mandates') {
                setView('mandates');
              } else {
                setView('agents', { agent_kind: 'ai', agents_mode: mode });
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              fontFamily: monoFont,
              fontSize: 11,
              color: C.text,
              letterSpacing: 1,
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            OPEN IN AGENTS VIEW →
          </button>
        </div>
      </div>
    </Drawer>
  );
}
